import { silentRequest } from "../../../utils/axiosConfig";
import { getOrCreateDeviceId } from "../../../utils/trustedDevice";
import type {
  PatchTrustedDeviceBody,
  RegisterTrustedDeviceBody,
  TrustedDevice,
} from "../types";
import {
  extractTrustedDevice,
  extractTrustedDeviceArray,
} from "./responseParsers";

const BASE = "/api/trusted-devices";

export type AttendanceAccessErrorCode =
  | "MOBILE_NOT_ALLOWED"
  | "DEVICE_NOT_TRUSTED"
  | "DEVICE_REVOKED";

export async function registerTrustedDevice(
  body: RegisterTrustedDeviceBody,
): Promise<TrustedDevice | null> {
  const res = await silentRequest.post<unknown>(BASE, body);
  return extractTrustedDevice(res.data);
}

export async function fetchMyTrustedDevices(): Promise<TrustedDevice[]> {
  const res = await silentRequest.get<unknown>(`${BASE}/me`);
  return extractTrustedDeviceArray(res.data);
}

export async function fetchTrustedDevicesForUser(
  userId: string,
): Promise<TrustedDevice[]> {
  if (!userId.trim()) {
    throw new Error("userId is required");
  }
  const res = await silentRequest.get<unknown>(
    `${BASE}?userId=${encodeURIComponent(userId.trim())}`,
  );
  return extractTrustedDeviceArray(res.data);
}

export async function patchTrustedDevice(
  id: string,
  body: PatchTrustedDeviceBody,
): Promise<TrustedDevice | null> {
  const res = await silentRequest.patch<unknown>(`${BASE}/${id}`, body);
  return extractTrustedDevice(res.data);
}

/** Reads API error payload for attendance / trusted-device flows. */
export function readApiErrorPayload(error: unknown): {
  message: string;
  code?: string;
  deviceId?: string;
} {
  const err = error as {
    response?: { data?: Record<string, unknown> };
    message?: string;
  };
  const data = err.response?.data ?? {};
  const message =
    (typeof data.message === "string" && data.message) ||
    (typeof data.error === "string" && data.error) ||
    err.message ||
    "Request failed.";

  const code =
    (typeof data.code === "string" && data.code) ||
    (typeof data.errorCode === "string" && data.errorCode) ||
    undefined;

  const deviceId =
    (typeof data.deviceId === "string" && data.deviceId) ||
    (typeof data.requestedDeviceId === "string" && data.requestedDeviceId) ||
    undefined;

  return { message, code, deviceId };
}

export function getAttendanceAccessErrorCode(
  error: unknown,
): AttendanceAccessErrorCode | undefined {
  const { code } = readApiErrorPayload(error);
  if (
    code === "MOBILE_NOT_ALLOWED" ||
    code === "DEVICE_NOT_TRUSTED" ||
    code === "DEVICE_REVOKED"
  ) {
    return code;
  }
  return undefined;
}

export function isDeviceNotTrustedError(error: unknown): boolean {
  const code = getAttendanceAccessErrorCode(error);
  if (code === "DEVICE_NOT_TRUSTED" || code === "DEVICE_REVOKED") {
    return true;
  }
  const { message } = readApiErrorPayload(error);
  return /device_not_trusted|device_revoked/i.test(message);
}

export function isMobileNotAllowedError(error: unknown): boolean {
  return getAttendanceAccessErrorCode(error) === "MOBILE_NOT_ALLOWED";
}

export interface AttendanceClockErrorHandlers {
  onTrustedDeviceRequired: (deviceId: string, message: string) => void;
  showError: (message: string) => void;
  showInfo?: (message: string) => void;
}

/** Shared handler for time-in / time-out device and mobile guard errors. */
export function handleAttendanceClockError(
  error: unknown,
  handlers: AttendanceClockErrorHandlers,
): void {
  const { message, code, deviceId } = readApiErrorPayload(error);
  const accessCode = getAttendanceAccessErrorCode(error);

  if (accessCode === "MOBILE_NOT_ALLOWED") {
    handlers.showError(
      message ||
        "Clock-out from mobile browsers is blocked. Please use your office laptop.",
    );
    return;
  }

  if (
    accessCode === "DEVICE_NOT_TRUSTED" ||
    accessCode === "DEVICE_REVOKED" ||
    isDeviceNotTrustedError(error)
  ) {
    handlers.onTrustedDeviceRequired(
      deviceId || getOrCreateDeviceId(),
      message ||
        (code === "DEVICE_REVOKED"
          ? "This device has been revoked. Contact HR to register a new trusted device."
          : "This device must be registered and approved by HR before clock-out."),
    );
    if (handlers.showInfo) {
      handlers.showInfo(
        message ||
          "This device must be registered and approved by HR before clock-out.",
      );
    }
    return;
  }

  handlers.showError(message);
}

/** POST time-in with error toasts suppressed — caller handles device errors. */
export async function postTimeIn(body: Record<string, unknown>) {
  return silentRequest.post("/api/attendance/time-in", body);
}

/** POST time-out with error toasts suppressed — caller handles device errors. */
export async function postTimeOut(body: Record<string, unknown>) {
  return silentRequest.post("/api/attendance/time-out", body);
}
